// Orden y agrupación de tarjetas según la especificación:
// grupo de Status (to_do → in_progress → complete) → opción de Status en el orden listado →
// prioridad (ALTA, MEDIA, BAJA, sin prioridad) → Fechas.start ascendente (sin fecha al final) →
// Fecha de creación ascendente. El id desempata para que el orden sea total y estable.

import {
  GROUP_ORDER,
  normalizeStatus,
  statusGroup,
  statusRank,
  type StatusGroup,
} from './status.ts';
import { priorityRank } from './priority.ts';

export interface SortableCard {
  id: string;
  status: string | null;
  etiquetas: readonly string[];
  /** Instante ISO (UTC) del inicio de `Fechas`, o null si no tiene fecha. */
  fechas_start: string | null;
  /** Instante ISO de `Fecha de creación`. */
  fecha_creacion: string;
}

function time(iso: string | null): number | null {
  if (iso === null) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

export function compareCards(a: SortableCard, b: SortableCard): number {
  const ga = GROUP_ORDER.indexOf(statusGroup(a.status));
  const gb = GROUP_ORDER.indexOf(statusGroup(b.status));
  if (ga !== gb) return ga - gb;

  const sa = statusRank(a.status);
  const sb = statusRank(b.status);
  if (sa !== sb) return sa - sb;
  // Dos Status desconocidos distintos: orden alfabético estable.
  const na = normalizeStatus(a.status);
  const nb = normalizeStatus(b.status);
  if (na !== nb) return na < nb ? -1 : 1;

  const pa = priorityRank(a.etiquetas);
  const pb = priorityRank(b.etiquetas);
  if (pa !== pb) return pa - pb;

  const fa = time(a.fechas_start);
  const fb = time(b.fechas_start);
  if (fa !== fb) {
    if (fa === null) return 1;
    if (fb === null) return -1;
    return fa - fb;
  }

  const ca = time(a.fecha_creacion) ?? 0;
  const cb = time(b.fecha_creacion) ?? 0;
  if (ca !== cb) return ca - cb;

  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function sortCards<T extends SortableCard>(cards: readonly T[]): T[] {
  return [...cards].sort(compareCards);
}

export interface StatusSection<T> {
  status: string;
  group: StatusGroup;
  cards: T[];
}

/** Agrupa por Status (solo secciones no vacías), en el orden definido. */
export function groupByStatus<T extends SortableCard>(cards: readonly T[]): StatusSection<T>[] {
  const sections: StatusSection<T>[] = [];
  for (const card of sortCards(cards)) {
    const status = normalizeStatus(card.status);
    const last = sections[sections.length - 1];
    if (last && last.status === status) {
      last.cards.push(card);
    } else {
      sections.push({ status, group: statusGroup(status), cards: [card] });
    }
  }
  return sections;
}
